/**
 * Enhanced Express middleware with all features built-in
 */

import type { Request, Response, NextFunction } from 'express';
import { AgentDetector } from '@kya-os/agentshield';
import { createStorageAdapter } from './storage';
import type { 
  StorageAdapter, 
  StorageConfig, 
  AgentDetectionEvent
} from './storage/types';
import type { DetectionResult } from '@kya-os/agentshield';

/**
 * Enhanced configuration with built-in features
 */
export interface EnhancedMiddlewareConfig {
  /**
   * Storage configuration
   */
  storage?: StorageConfig;
  
  /**
   * Session tracking configuration
   */
  sessionTracking?: {
    enabled?: boolean;
    ttl?: number;
  };
  
  /**
   * Paths to skip detection
   */
  skipPaths?: string[];
  
  /**
   * Action when agent detected
   */
  onAgentDetected?: 'block' | 'log' | 'allow';
  
  /**
   * Custom handler for agent detection
   */
  onDetection?: (result: DetectionResult, req: Request) => void | Promise<void>;
  
  /**
   * Confidence threshold
   */
  confidenceThreshold?: number;
  
  /**
   * Response when blocking
   */
  blockedResponse?: {
    status?: number;
    message?: string;
  };
  
  /**
   * Enable WASM (if available)
   */
  enableWasm?: boolean;
}

/**
 * Session tracking helper
 */
class SessionManager {
  private sessionLastActivity = new Map<string, number>();
  
  generateSessionId(ipAddress?: string, userAgent?: string): string {
    const now = Date.now();
    const timeWindow = Math.floor(now / (5 * 60 * 1000)); // 5-minute windows
    const baseKey = `${ipAddress || 'unknown'}:${userAgent || 'unknown'}`;
    const windowKey = `${baseKey}:${timeWindow}`;
    
    const lastActivity = this.sessionLastActivity.get(windowKey);
    const shouldCreateNewSession = !lastActivity || now - lastActivity > 30000;
    
    this.sessionLastActivity.set(windowKey, now);
    
    // Clean up old entries
    if (this.sessionLastActivity.size > 100) {
      const cutoff = now - 600000;
      for (const [key, time] of this.sessionLastActivity.entries()) {
        if (time < cutoff) {
          this.sessionLastActivity.delete(key);
        }
      }
    }
    
    const data = shouldCreateNewSession 
      ? `${windowKey}:${now}` 
      : `${windowKey}:${lastActivity}`;
    
    // Simple hash
    let hash = 0;
    for (let i = 0; i < data.length; i++) {
      const char = data.charCodeAt(i);
      hash = (hash << 5) - hash + char;
      hash = hash & hash;
    }
    
    return Math.abs(hash).toString(16).padStart(12, '0').substring(0, 12);
  }
}

/**
 * Get verification method based on detection
 */
function getVerificationMethod(
  confidence: number,
  reasons: string[] = []
): string {
  // Check for signature verification
  if (reasons.some(r => 
    r.includes('signature_agent') && 
    !r.includes('signature_headers_present')
  )) {
    return 'signature';
  }
  
  // Check for WASM enhancement
  if (confidence >= 0.85 && confidence < 1.0) {
    return 'wasm-enhanced';
  }
  
  // Default to pattern matching
  return 'pattern';
}

/**
 * Apply WASM confidence boost for enhanced detection
 */
function getWasmConfidenceBoost(
  baseConfidence: number,
  reasons: string[] = []
): number {
  // If signature verification succeeded, always return 100%
  if (reasons.some(r => 
    r.includes('signature_agent') && 
    !r.includes('signature_headers_present')
  )) {
    return 1.0;
  }
  
  // For high-confidence pattern detection, boost to 95%
  if (baseConfidence >= 0.85) {
    return 0.95;
  }
  
  // For medium-high confidence, apply smaller boost
  if (baseConfidence >= 0.7) {
    return Math.min(baseConfidence * 1.1, 0.9);
  }
  
  // No boost for lower confidence
  return baseConfidence;
}

/**
 * Pattern detection for suspicious browser-like requests
 */
function detectSuspiciousPatterns(req: Request): {
  confidence: number;
  reasons: string[];
  detectedAgent?: { type: string; name: string };
} {
  const headers = req.headers as Record<string, string>;
  const normalizedHeaders: Record<string, string> = {};
  
  // Normalize headers to lowercase
  for (const [key, value] of Object.entries(headers)) {
    normalizedHeaders[key.toLowerCase()] = value;
  }
  
  const userAgent = normalizedHeaders['user-agent'] || '';
  const hasChrome = userAgent.toLowerCase().includes('chrome');
  const hasFirefox = userAgent.toLowerCase().includes('firefox');
  const hasSafari = userAgent.toLowerCase().includes('safari');
  const hasBrowserUA = hasChrome || hasFirefox || hasSafari;
  
  // Check for browser-specific headers that real browsers send
  const hasSecChUa = !!normalizedHeaders['sec-ch-ua'];
  const hasSecFetchHeaders = !!normalizedHeaders['sec-fetch-site'];
  const hasCookies = !!normalizedHeaders['cookie'];
  const hasAcceptLanguage = !!normalizedHeaders['accept-language'];
  
  const reasons: string[] = [];
  let confidence = 0;
  let detectedAgent: { type: string; name: string } | undefined;
  
  // Pattern 1: Browser UA but missing critical browser headers (like Perplexity)
  if (hasBrowserUA) {
    const missingHeaders = [];
    if (!hasSecChUa && hasChrome) missingHeaders.push('sec-ch-ua');
    if (!hasSecFetchHeaders) missingHeaders.push('sec-fetch-*');
    if (!hasAcceptLanguage) missingHeaders.push('accept-language');
    if (!hasCookies) missingHeaders.push('cookies');
    
    if (missingHeaders.length >= 2) {
      // Multiple missing headers = likely AI agent masquerading
      confidence = 0.85;
      reasons.push('browser_ua_missing_headers');
      reasons.push(`missing:${missingHeaders.join(',')}`);
      
      // Try to identify which agent based on patterns
      if (hasChrome && !hasSecChUa) {
        // Perplexity often uses Chrome UA without Sec-CH-UA
        detectedAgent = { type: 'perplexity', name: 'Perplexity' };
      } else {
        detectedAgent = { type: 'unknown', name: 'Unknown AI Agent' };
      }
    }
  }
  
  // Pattern 2: Check for cloud provider IPs
  const xForwardedFor = normalizedHeaders['x-forwarded-for'];
  const ip = req.ip || xForwardedFor?.split(',')[0];
  if (ip) {
    const cloudProviders = {
      aws: ['54.', '52.', '35.', '18.', '3.'],
      gcp: ['35.', '34.', '104.', '107.', '108.'],
      azure: ['13.', '20.', '40.', '52.', '104.'],
    };
    
    for (const [provider, prefixes] of Object.entries(cloudProviders)) {
      if (prefixes.some(prefix => ip.startsWith(prefix))) {
        confidence = Math.max(confidence, 0.4);
        reasons.push(`cloud_provider:${provider}`);
        break;
      }
    }
  }
  
  // Pattern 3: Minimal headers (bots often send very few headers)
  const headerCount = Object.keys(normalizedHeaders).length;
  if (headerCount < 8 && hasBrowserUA) {
    confidence = Math.max(confidence, 0.6);
    reasons.push(`minimal_headers:${headerCount}`);
  }
  
  // Boost confidence for multiple signals
  if (reasons.length >= 3) {
    confidence = Math.min(confidence * 1.15, 0.95);
  }
  
  return { 
    confidence, 
    reasons, 
    ...(detectedAgent && { detectedAgent })
  };
}

/**
 * Create enhanced AgentShield middleware with all features
 */
export function createEnhancedAgentShieldMiddleware(config: EnhancedMiddlewareConfig = {}) {
  // Initialize storage adapter
  let storageAdapter: StorageAdapter | null = null;
  let storageInitPromise: Promise<StorageAdapter> | null = null;
  
  const getStorage = async () => {
    if (storageAdapter) return storageAdapter;
    if (storageInitPromise) return storageInitPromise;
    
    storageInitPromise = createStorageAdapter(config.storage).then(adapter => {
      storageAdapter = adapter;
      return adapter;
    });
    
    return storageInitPromise;
  };
  
  // Initialize detector with lazy loading (try WASM first if available)
  let detector: AgentDetector | null = null;
  let detectorInitPromise: Promise<void> | null = null;
  
  const getDetector = async () => {
    if (detector) return detector;
    if (detectorInitPromise) {
      await detectorInitPromise;
      return detector!;
    }
    
    detectorInitPromise = (async () => {
      // For Express, we always use the standard AgentDetector
      // which includes WASM support internally
      detector = new AgentDetector();
    })();
    
    await detectorInitPromise;
    return detector!;
  };
  
  // Session manager
  const sessionManager = new SessionManager();
  const sessionTrackingEnabled = config.sessionTracking?.enabled !== false;
  
  // Return the middleware function
  return async (req: Request, res: Response, next: NextFunction): Promise<void> => {
    const pathname = req.path;
    
    // Check if path should be skipped
    if (config.skipPaths?.some(path => pathname.startsWith(path))) {
      return next();
    }
    
    // Extract request context
    const userAgent = req.get('user-agent');
    const ipAddress = req.ip || req.get('x-forwarded-for') || req.socket.remoteAddress;
    
    const context = {
      userAgent: userAgent || '',
      ipAddress: ipAddress || '',
      headers: req.headers as Record<string, string>,
      url: req.originalUrl,
      method: req.method,
      timestamp: new Date(),
    };
    
    try {
      // Run detection with lazy-loaded detector
      const activeDetector = await getDetector();
      let result = await activeDetector.analyze(context);
      
      // If WASM didn't detect anything, try pattern detection
      if (!result.isAgent || result.confidence < 0.3) {
        const patternResult = detectSuspiciousPatterns(req);
        
        if (patternResult.confidence > result.confidence) {
          // Pattern detection found something WASM missed
          result = {
            ...result,
            isAgent: patternResult.confidence > 0.3,
            confidence: patternResult.confidence,
            reasons: patternResult.reasons,
            detectedAgent: patternResult.detectedAgent,
            verificationMethod: 'pattern',
            confidenceLevel: 
              patternResult.confidence >= 0.8 ? 'high' :
              patternResult.confidence >= 0.5 ? 'medium' : 'low',
          };
        }
      }
      
      // Apply WASM confidence boost if applicable
      let finalConfidence = result.confidence;
      let verificationMethod = result.verificationMethod || 'pattern';
      
      if (result.isAgent) {
        const reasons = (result as any).reasons || [];
        
        if (config.enableWasm !== false && finalConfidence >= 0.85 && finalConfidence < 1.0) {
          finalConfidence = getWasmConfidenceBoost(result.confidence, reasons);
          verificationMethod = getVerificationMethod(finalConfidence, reasons);
        }
      }
      
      // Store detection info in request for later use
      (req as any).agentShield = {
        detected: result.isAgent,
        confidence: finalConfidence,
        agent: result.detectedAgent,
        verificationMethod,
      };
      
      // Handle high-confidence detection
      if (result.isAgent && finalConfidence >= (config.confidenceThreshold ?? 0.7)) {
        // Store event if storage is configured and session tracking is enabled
        if (sessionTrackingEnabled) {
          const storage = await getStorage();
          const sessionId = sessionManager.generateSessionId(ipAddress || undefined, userAgent || undefined);
          
          // Create event
          const event: AgentDetectionEvent = {
            eventId: `agent_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`,
            sessionId,
            timestamp: new Date().toISOString(),
            agentType: result.detectedAgent?.type || 'unknown',
            agentName: result.detectedAgent?.name || 'Unknown',
            confidence: finalConfidence,
            path: req.originalUrl,
            ...(userAgent && { userAgent }),
            ...(ipAddress && { ipAddress }),
            method: req.method,
            detectionReasons: (result as any).reasons || [],
            verificationMethod,
            detectionDetails: {
              patterns: (result.detectedAgent as any)?.patterns,
              behaviors: (result.detectedAgent as any)?.behaviors,
              fingerprintMatches: (result.detectedAgent as any)?.fingerprints,
            },
          };
          
          // Store event
          try {
            await storage.storeEvent(event);
            
            // Get or create session
            let session = await storage.getSession(sessionId);
            
            if (session) {
              // Update existing session
              session.lastSeen = event.timestamp;
              session.eventCount++;
              if (!session.paths.includes(req.originalUrl)) {
                session.paths.push(req.originalUrl);
              }
              session.averageConfidence = 
                (session.averageConfidence * (session.eventCount - 1) + finalConfidence) / 
                session.eventCount;
              if (!session.verificationMethods.includes(verificationMethod)) {
                session.verificationMethods.push(verificationMethod);
              }
            } else {
              // Create new session
              session = {
                sessionId,
                ...(ipAddress && { ipAddress }),
                ...(userAgent && { userAgent }),
                agentType: result.detectedAgent?.type || 'unknown',
                agentName: result.detectedAgent?.name || 'Unknown',
                firstSeen: event.timestamp,
                lastSeen: event.timestamp,
                eventCount: 1,
                paths: [req.originalUrl],
                averageConfidence: finalConfidence,
                verificationMethods: [verificationMethod],
              };
            }
            
            if (session) {
              await storage.storeSession(session);
            }
          } catch (error) {
            console.error('[AgentShield] Failed to store event:', error);
          }
        }
        
        // Call custom handler if provided
        if (config.onDetection) {
          await config.onDetection(result, req);
        }
        
        // Set response headers
        res.setHeader('x-agentshield-detected', 'true');
        res.setHeader('x-agentshield-confidence', String(Math.round(finalConfidence * 100)));
        res.setHeader('x-agentshield-agent', result.detectedAgent?.name || 'Unknown');
        res.setHeader('x-agentshield-verification', verificationMethod);
        
        // Add custom headers for high confidence
        if (finalConfidence > 0.9) {
          res.setHeader('x-ai-visitor', 'true');
          res.setHeader('x-ai-confidence', finalConfidence.toString());
          res.setHeader('x-ai-verification', verificationMethod);
        }
        
        // Handle based on configuration
        switch (config.onAgentDetected) {
          case 'block': {
            const { status = 403, message = 'Access denied: AI agent detected' } = 
              config.blockedResponse || {};
            
            res.status(status).json({
              error: message,
              detected: true,
              confidence: finalConfidence,
            });
            return;
          }
          
          case 'log':
            console.log(`[AgentShield] 🤖 AI Agent detected (${verificationMethod}):`, {
              agent: result.detectedAgent?.name,
              confidence: `${(finalConfidence * 100).toFixed(0)}%`,
              path: req.originalUrl,
              verification: verificationMethod,
            });
            break;
          
          case 'allow':
          default:
            // Continue processing
            break;
        }
      }
      
      // Continue to next middleware
      next();
    } catch (error) {
      console.error('[AgentShield] Error during detection:', error);
      // Don't block on errors
      next();
    }
  };
}

/**
 * Export storage types for user access
 */
export type { StorageAdapter, StorageConfig, AgentDetectionEvent, AgentSession } from './storage/types';